import TopNav from "../components/TopNav.jsx";
import ProductMark from "../components/ProductMark.jsx";
import FreeConsultWidget from "../components/FreeConsultWidget.jsx";

/**
 * MedicPage · /medic
 * Free forever · patient-education companion for verified nurses, EMTs,
 * community health workers, and any person caring for someone else.
 */
export default function MedicPage() {
  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain" data-testid="medic-page">
      <TopNav />
      <div className="mx-auto max-w-4xl px-4 md:px-8 py-12">
        <div className="text-[10px] uppercase tracking-[0.3em] text-sage-accent mb-3">FREE FOREVER · PUBLIC-SERVICE TIER</div>
        <div className="flex items-start gap-4 mb-4">
          <div className="text-sage-accent shrink-0"><ProductMark slug="medic" size={48} /></div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tighter uppercase">Medic</h1>
        </div>
        <p className="text-lg text-sage-muted leading-relaxed max-w-3xl mb-10 sage-prose">
          A plain-English medical companion. Free forever. For patients, families, community health workers, and anyone caring for someone else. Sage translates the chart, the pill bottle, and the after-visit summary — and tells you clearly when a real clinician needs to see this.
        </p>

        <div className="border border-sage-border p-6 mb-10">
          <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-3">Who it's for</div>
          <ul className="list-disc list-inside text-sm text-sage-text space-y-2 sage-prose">
            <li>Patients trying to understand a diagnosis or after-visit summary</li>
            <li>Family caregivers coordinating medications and appointments</li>
            <li>Community health workers and home-visit nurses</li>
            <li>Anyone reading a lab result in a hospital hallway at 2 a.m.</li>
          </ul>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-10">
          <div className="border border-sage-border p-4" data-testid="medic-feature-1">
            <div className="text-sm font-bold uppercase tracking-tight text-sage-text mb-1">Chart in plain English</div>
            <div className="text-xs text-sage-muted sage-prose">Paste a lab, a discharge letter, or a diagnosis. Get a clean summary a non-clinician can read.</div>
          </div>
          <div className="border border-sage-border p-4" data-testid="medic-feature-2">
            <div className="text-sm font-bold uppercase tracking-tight text-sage-text mb-1">Medication check</div>
            <div className="text-xs text-sage-muted sage-prose">Sanity-check a list for known interactions and dose ranges. Not a substitute for a pharmacist.</div>
          </div>
          <div className="border border-sage-border p-4" data-testid="medic-feature-3">
            <div className="text-sm font-bold uppercase tracking-tight text-sage-text mb-1">Appointment prep</div>
            <div className="text-xs text-sage-muted sage-prose">Turn a jumble of concerns into a clear list of questions to bring to your provider.</div>
          </div>
          <div className="border border-sage-border p-4" data-testid="medic-feature-4">
            <div className="text-sm font-bold uppercase tracking-tight text-sage-text mb-1">Red-flag guardrails</div>
            <div className="text-xs text-sage-muted sage-prose">If your description matches a serious pattern, Sage tells you to call 911 or urgent care — and stops answering the surface question.</div>
          </div>
        </div>

        <div className="mb-10">
          <h2 className="text-2xl font-bold uppercase tracking-tight text-sage-text mb-3">Ask a medical question — anonymous</h2>
          <FreeConsultWidget
            role="medic-patient-education"
            productSlug="medic"
            placeholder="Describe what you're trying to understand — a lab, a diagnosis, a medication, a decision. Plain English is fine."
            ctaLabel="Ask Medic"
            disclaimer="Medic is patient education, not medical advice. It is not a doctor, does not diagnose, and does not prescribe. In an emergency, call 911. For anything urgent, call your provider or a nurse line."
          />
        </div>

        <div className="border-t border-sage-border pt-6 text-xs text-sage-muted sage-prose">
          Medic stays free forever. The greedless pledge in ink. If a clinic, community health center, or non-profit wants Medic wired into intake or discharge, reach out through the Contact door.
        </div>
      </div>
    </div>
  );
}
